/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import {
  makeStyles,
  Tooltip,
  Typography,
  Grid,
  IconButton,
} from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import {
  EnvironmentHealthFields,
  EnvironmentHealthResultsFields,
  EnvironmentHealthStatusEnum,
} from 'modules/monitor/interfaces';
import clsx from 'clsx';
import { cyrb53Hash } from 'utils/helpers';
import { getCompanyName } from 'utils/tsHelpers';
import { useTableFilters } from 'hooks/useTableFilters';
import styles from './styles';

const initialQuery = {
  offset: 0,
  limit: 50,
  status: '',
  search: '',
};

interface Props {
  isLoading: boolean;
  environmentHealthData: EnvironmentHealthFields;
  modalClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const EnvironmentHealthTable = ({
  isLoading,
  environmentHealthData,
  modalClose,
  selectCompany,
  selectPartner,
}: Props) => {
  const headers = [
    {
      key: '',
      i18n: i18n.t('ACCESS_KEY'),
      centerTitle: true,
    },
    {
      key: 'partner',
      i18n: i18n.t('MONITOR.PARTNER_NAME'),
      order: false,
    },
    {
      key: 'environment',
      i18n: i18n.t('MONITOR.ENVIRONMENT_NAME'),
      order: false,
    },
    {
      key: 'robo_type',
      i18n: i18n.t('MONITOR.ROBO_TYPE'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'status',
      i18n: i18n.t('MONITOR.STATUS'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'instances',
      i18n: i18n.t('MONITOR.INSTANCES'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'temperature',
      i18n: i18n.t('MONITOR.TEMPERATURE'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'connected',
      i18n: i18n.t('MONITOR.CONNECTED'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'licenses_total',
      i18n: i18n.t('MONITOR.LICENSES_TOTAL'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'vacancies',
      i18n: i18n.t('MONITOR.VACANCIES'),
      order: false,
      centerTitle: true,
    },
  ];

  const { handleLimitChange, handlePageChange } = useTableFilters(initialQuery);

  const useStyles = makeStyles(styles());
  const classes = useStyles();
  const history = useHistory();

  const redirectToContext = (item: EnvironmentHealthResultsFields) => {
    selectCompany({
      id: item?.company.uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      id: item?.partner.uuid,
      name: item?.partner.name,
      shortName: item?.partner.name,
    });
    history.push(`/environments/${item?.environment.uuid}/instances`);
  };

  return (
    <Grid className={classes.table}>
      <Grid className={classes.titleBackground}>
        <Typography className={classes.tableTitle}>
          {i18n.t('MONITOR.ENVIRONMENT_HEALTH_TITLE')}
        </Typography>
      </Grid>
      <TableItems
        header={
          <>
            {headers.map(item => (
              <TableCell centerTitle={item?.centerTitle} key={item?.key}>
                {item?.i18n}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: EnvironmentHealthResultsFields) => (
          <TableRow
            key={cyrb53Hash(item?.environment?.name)}
            className={clsx(
              classes.tableRow,
              item?.status === EnvironmentHealthStatusEnum.ANALYZE &&
                classes.envToAnalyze
            )}
          >
            <TableCell centerTitle textAlignCenter>
              <IconButton
                className={classes.accessButton}
                onClick={() => {
                  modalClose();
                  redirectToContext(item);
                }}
              >
                <InputIcon />
              </IconButton>
            </TableCell>
            <TableCell className={classes.text}>
              <Tooltip title={item?.partner.name}>
                <Typography>{item?.partner.name}</Typography>
              </Tooltip>
            </TableCell>
            <TableCell className={classes.text}>
              <Tooltip title={item?.environment.name}>
                <Typography>{item?.environment.name}</Typography>
              </Tooltip>
            </TableCell>
            <TableCell centerTitle>
              {item?.environment.publication_type}
            </TableCell>
            <TableCell
              centerTitle
              className={clsx(
                classes.status,
                item?.status === EnvironmentHealthStatusEnum.ANALYZE &&
                  classes.analyzeStatus
              )}
            >
              {i18n.t(`MONITOR.${item?.status.toUpperCase()}_STATUS_KEY`)}
            </TableCell>
            <TableCell centerTitle>{item?.instances_running}</TableCell>
            <TableCell centerTitle>{item?.avg_temperature}</TableCell>
            <TableCell centerTitle>{item?.connections}</TableCell>
            <TableCell centerTitle>{item?.total_licenses}</TableCell>
            <TableCell centerTitle>{item?.vacancies}</TableCell>
          </TableRow>
        )}
        data={environmentHealthData}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={
          <Typography className={classes.notFoundText}>
            {i18n.t('MONITOR.NO_RESULTS_ENVIRONMENT_HEALTH')}
          </Typography>
        }
        notFooter
      />
    </Grid>
  );
};

const mapStateToProps = ({ monitor: { isLoading } }: ApplicationState) => ({
  isLoading,
});

export default connect(mapStateToProps, {
  modalClose,
  selectCompany,
  selectPartner,
})(EnvironmentHealthTable);
